import React, {useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as WarningIcon} from 'assets/svg/warning.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Modal} from 'components/ui-kit/Modal';

import {Textarea} from 'components/ui-kit/Textarea';

import {colors} from 'theme';

import {
    Content,
    Footer,
    InfoWrapper,
    WarningCard,
    WarningCardContent,
    WarningCardIcon,
    WarningDescription,
    WarningTitle,
} from './styled';

type RejectProductModalProps = {
    isPending: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    renderInline?: boolean;
    show: boolean;
};

export const RejectProductModal = ({
    isPending,
    onClose,
    onConfirm,
    renderInline = false,
    show,
}: RejectProductModalProps) => {
    const {t} = useTranslation();
    const [reason, setReason] = useState('');
    const allowedToSubmit = useMemo(() => reason.trim().length > 0, [reason]);

    const content = (
        <Content>
            <InfoWrapper>
                <WarningCard>
                    <WarningCardIcon>
                        <Icon
                            source={WarningIcon}
                            width={20}
                            height={20}
                            fillColor={colors.warningOrangeSecondary}
                            strokeColor={colors.warningOrange}
                        />
                    </WarningCardIcon>
                    <WarningCardContent>
                        <WarningTitle>
                            {t('pages.registries.rejectProductModal.warningTitle', {
                                defaultValue: 'Reject Product Card',
                            })}
                        </WarningTitle>
                        <WarningDescription>
                            {t('pages.registries.rejectProductModal.warningDescription', {
                                defaultValue:
                                    'Please provide a reason for rejecting this product card. The author will receive this feedback.',
                            })}
                        </WarningDescription>
                    </WarningCardContent>
                </WarningCard>
                <Textarea
                    updatedStyle
                    label={t('pages.registries.rejectProductModal.reasonLabel', {
                        defaultValue: 'Reason for rejection *',
                    })}
                    placeholder={t('pages.registries.rejectProductModal.reasonPlaceholder', {
                        defaultValue: 'Describe why you are rejecting this product card...',
                    })}
                    formFieldName="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </InfoWrapper>
            <Footer>
                <Button
                    variant={ButtonVariant.Outline}
                    type="button"
                    disabled={isPending}
                    onClick={onClose}
                >
                    {t('components.form.Cancel')}
                </Button>
                <Button
                    variant={ButtonVariant.Normal}
                    type="button"
                    disabled={isPending || !allowedToSubmit}
                    onClick={() => onConfirm(reason)}
                >
                    {t('pages.registries.rejectProductModal.confirmButton', {
                        defaultValue: 'Confirm Rejection',
                    })}
                </Button>
            </Footer>
        </Content>
    );

    if (renderInline) return content;

    return (
        <Modal show={show} onClose={onClose} modalWidth={1}>
            {content}
        </Modal>
    );
};
